import { motion } from 'framer-motion';
import {
  Children,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { capTableNestedTableClass } from '../desktop-details/table-variants/cap-table-sticky-classes';

/**
 * One timing for every height change in the cap table and its modals: the expanding row, the
 * Ordering tab's Custom section, and the Customize View modal resizing on a tab switch. 300ms to
 * the millisecond also matches the `transition-colors` the rows use — the panel growing and the
 * surface changing colour are one gesture, and even 20ms apart they read as two.
 *
 * The CSS pair exists for the places driven through the DOM rather than by framer. The curve is
 * framer's own `easeInOut`, spelled out so the two can never quietly diverge.
 */
export const CAP_TABLE_ROW_EXPAND_MS = 300;

export const CAP_TABLE_ROW_EXPAND_EASING = 'cubic-bezier(0.42, 0, 0.58, 1)';

export const CAP_TABLE_ROW_EXPAND_TRANSITION = {
  duration: CAP_TABLE_ROW_EXPAND_MS / 1000,
  ease: 'easeInOut',
} as const;

export const CAP_TABLE_REVEAL_TRANSITION = {
  duration: 0.22,
  ease: 'easeOut',
} as const;

type CapTableExpandedRowsPanelProps = {
  children: ReactNode;
  footer?: ReactNode;
  isExpanded: boolean;
  isExtraRowsOpen?: boolean;
  onExpandedVisualChange?: (isExpandedVisual: boolean) => void;
  onExtraCollapseComplete?: () => void;
  pinnedRowCount?: number;
};

export const CapTableExpandedRowsPanel: FC<CapTableExpandedRowsPanelProps> = ({
  isExpanded,
  isExtraRowsOpen = true,
  children,
  footer,
  onExpandedVisualChange,
  onExtraCollapseComplete,
  pinnedRowCount = 0,
}) => {
  const cellRef = useRef<HTMLTableCellElement>(null);
  const [columnWidths, setColumnWidths] = useState<number[]>([]);

  const childArray = Children.toArray(children);
  const pinnedRows = pinnedRowCount > 0 ? childArray.slice(0, pinnedRowCount) : childArray;
  const extraRows = pinnedRowCount > 0 ? childArray.slice(pinnedRowCount) : [];

  const syncColumnWidths = useCallback(() => {
    const table = cellRef.current?.closest('table');
    if (!table) return;

    const headerCells = table.querySelectorAll('thead th');
    if (!headerCells.length) return;

    setColumnWidths(Array.from(headerCells, (cell) => cell.getBoundingClientRect().width));
  }, []);

  useLayoutEffect(() => {
    if (!isExpanded) return;

    syncColumnWidths();

    const table = cellRef.current?.closest('table');
    if (!table) return;

    const resizeObserver = new ResizeObserver(syncColumnWidths);
    resizeObserver.observe(table);

    return () => resizeObserver.disconnect();
  }, [isExpanded, syncColumnWidths, children]);

  /**
   * Raised as the panel starts opening so the surface fades in with it, and — see
   * `onAnimationComplete` below — not lowered until the panel has finished closing, so the blue
   * and the accent bar stay for the whole collapse rather than leaving part-way through it.
   */
  useEffect(() => {
    if (isExpanded) {
      onExpandedVisualChange?.(true);
    }
  }, [isExpanded, onExpandedVisualChange]);

  const columnGroup =
    columnWidths.length > 0 ? (
      <colgroup>
        {columnWidths.map((width, index) => (
          <col key={index} style={{ width }} />
        ))}
      </colgroup>
    ) : null;

  return (
    <tr aria-hidden={!isExpanded}>
      <td className="bg-brand-25 p-0" colSpan={999} ref={cellRef}>
        <motion.div
          animate={isExpanded ? { height: 'auto' } : { height: 0 }}
          className="overflow-clip bg-brand-25"
          initial={false}
          onAnimationComplete={() => {
            syncColumnWidths();

            // Lowered only once the panel has shut, so the surface and the accent bar last exactly
            // as long as the collapse. The row styles fade *in* but not *out*, so this reads as
            // them ending with the animation rather than lingering past it.
            if (!isExpanded) {
              onExpandedVisualChange?.(false);
            }
          }}
          transition={CAP_TABLE_ROW_EXPAND_TRANSITION}
        >
          <table className={capTableNestedTableClass}>
            {columnGroup}
            <tbody>{pinnedRows}</tbody>
          </table>
          {extraRows.length > 0 && (
            <motion.div
              animate={isExtraRowsOpen ? { height: 'auto' } : { height: 0 }}
              className="overflow-clip bg-brand-25"
              initial={{ height: 0 }}
              onAnimationComplete={() => {
                if (!isExtraRowsOpen) onExtraCollapseComplete?.();
              }}
              transition={CAP_TABLE_ROW_EXPAND_TRANSITION}
            >
              <table className={capTableNestedTableClass}>
                {columnGroup}
                <tbody>{extraRows}</tbody>
              </table>
            </motion.div>
          )}
          {footer}
        </motion.div>
      </td>
    </tr>
  );
};
